import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { listTours, type TourListItem } from "@/lib/tours";
import TourCard from "@/pages/tours/components/TourCard";
import { onTourImageError } from "@/lib/imageFallback";

// Destination taxonomy (slug -> presentation keys + backend location name).
// Static reference data: BCS has a fixed set of destinations, not dynamic
// content. `name` is a do-not-translate proper noun; the prose (tagline, desc,
// facts) lives in the "translation" namespace under destinationPages.<i18nKey>.
const destinationContent: Record<
  string,
  { name: string; i18nKey: string; image: string }
> = {
  "la-paz": {
    name: "La Paz",
    i18nKey: "laPaz",
    image: "https://images.unsplash.com/photo-1505228395891-9a51e7e86bf6?w=1200&q=80&auto=format&fit=crop",
  },
  "los-cabos": {
    name: "Los Cabos",
    i18nKey: "losCabos",
    image: "https://images.unsplash.com/photo-1518391846015-55a9cc003b25?w=1200&q=80&auto=format&fit=crop",
  },
  "cabo-san-lucas": {
    name: "Cabo San Lucas",
    i18nKey: "caboSanLucas",
    image: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1200&q=80&auto=format&fit=crop",
  },
};

export default function DestinationPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation(["home", "translation", "tour"]);
  const priceLocale = i18n.language === "es" ? "es-MX" : "en-US";

  const content = slug ? destinationContent[slug] : undefined;
  const base = content ? `destinationPages.${content.i18nKey}` : "";
  const facts = content
    ? [
        t(`${base}.fact1`, { ns: "translation" }),
        t(`${base}.fact2`, { ns: "translation" }),
        t(`${base}.fact3`, { ns: "translation" }),
        t(`${base}.fact4`, { ns: "translation" }),
      ]
    : [];

  const [destTours, setDestTours] = useState<TourListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!content) return;
    let cancelled = false;
    setLoading(true);
    listTours({ location: content.name, pageSize: 60 })
      .then((data) => { if (!cancelled) setDestTours(data); })
      .catch(() => { if (!cancelled) setDestTours([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (!content) {
    return (
      <div className="min-h-screen bg-offwhite pt-20 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
            <i className="ri-map-pin-line text-4xl" />
          </div>
          <h1 className="text-xl font-bold text-charcoal mb-2">{t("noResults", { ns: "translation" })}</h1>
          <Link to="/tours" className="inline-flex bg-ocean text-white text-sm font-medium px-6 py-2.5 rounded-full">
            {t("backToTours", { ns: "tour" })}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20">
      {/* Hero */}
      <div className="relative h-[360px] md:h-[480px] overflow-hidden">
        <img src={content.image} alt={content.name} onError={onTourImageError} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
        <div className="absolute inset-0 flex items-end pb-10 md:pb-14 px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto w-full">
            <div className="flex items-center gap-2 text-white/80 text-xs mb-2">
              <Link to="/" className="hover:text-white transition-colors">{t("breadcrumb.home", { ns: "translation" })}</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/tours" className="hover:text-white transition-colors">{t("breadcrumb.tours", { ns: "translation" })}</Link>
              <i className="ri-arrow-right-s-line" />
              <span>{content.name}</span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-2">{content.name}</h1>
            <p className="text-white/80 text-sm max-w-xl">{t(`${base}.tagline`, { ns: "translation" })}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
        <div className="max-w-5xl mx-auto">
          {/* About + Facts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-14">
            <div>
              <h2 className="font-display text-xl md:text-2xl font-bold text-charcoal mb-4">{t("destinationPages.aboutTitle", { ns: "translation" })}</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{t(`${base}.desc`, { ns: "translation" })}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-charcoal text-sm mb-4">{t("destinationPages.highlightsTitle", { ns: "translation" })}</h3>
              <ul className="space-y-3">
                {facts.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-ocean/10 text-ocean shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs" />
                    </div>
                    <span className="text-sm text-gray-600">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Tours */}
          <h2 className="font-display text-xl md:text-2xl font-bold text-charcoal mb-6">
            {destTours.length} {t("home:categories.tourCount")} {t("toursIn", { ns: "translation" })} {content.name}
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[360px] bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : destTours.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
                <i className="ri-folder-unknow-line text-4xl" />
              </div>
              <h3 className="text-lg font-bold text-charcoal mb-2">{t("noResults", { ns: "translation" })}</h3>
              <p className="text-sm text-gray-500">{t("noResultsDesc", { ns: "translation" })}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {destTours.map((tour) => (
                <TourCard key={tour.id} tour={tour} priceLocale={priceLocale} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}